/**
 * A class's standings across the rounds it climbs, laid out from what
 * `GET /api/competitions/:slug/standings/:category` sends.
 *
 * **No regulation here.** Every row arrives with its place (`rank`) in the
 * table it sits in, worked out by the server by the competition's regulation
 * (`RankingStrategies`) — across the class's turns, a card keyed by the
 * problem's number within the class, so Blocco 1 of Turno 1 is Blocco 1 of
 * Turno 2. This only decides which tables the page draws and in what order the
 * rows go: placed first, by place; then the rest, by name.
 */

import { judgedIds } from './round-score-summary';
import type {
  CategoryStandings,
  IncomparableReason,
  StandingEntry,
  StandingGroup,
  StandingRound,
  StandingTie,
  Tiebreak,
} from '../services/category-standings';
import type { RoundType } from '../types/competition-round';

/** One row of a table: the athlete, and where they came (null: not placed yet). */
export interface StandingsRow {
  entry: StandingEntry;
  rank: number | null;
}

/**
 * One table of the page.
 *
 * `turns` is a class's turns merged into one; `turn` is one of them shown apart
 * because they could not be merged; `round` is a round of the class that is not
 * a turn.
 */
export interface StandingsSection {
  key: string;
  kind: 'turns' | 'turn' | 'round';
  rounds: StandingRound[];
  roundType: RoundType | null;
  /** Placed rows first, by place; then the rest. Ties and the rest by name. */
  rows: StandingsRow[];
  /** Whether the table says which turn each row climbed: two or more merged. */
  showTurn: boolean;
  /**
   * Lead: the class climbed partly on top rope and partly on lead here. A led
   * route beats a top roped one however far up each climber got, and a table
   * with both on it says so (see `RoundStandings`).
   */
  mixedStyles: boolean;
  /** The judged problems of the section's rounds, by id — what `tally` counts a share of. */
  judged: Set<string>;
  /** Their numbers within the class, for the heading to name. */
  judgedNumbers: number[];
  /**
   * The organisers' tie-breaks of this table (empty for anybody else): the
   * group's, narrowed to the athletes of a turn shown apart.
   */
  tiebreaks: Tiebreak[];
  /** The ties still level here, called or not. */
  ties: StandingTie[];
  /** The group a new tie-break is called on: null for the turns, else the round. */
  scopeRoundId: string | null;
  canAddTiebreak: boolean;
  /** The rounds a tie-break waits on, by name. */
  roundsNotEnded: string[];
}

export interface StandingsLayout {
  sections: StandingsSection[];
  /** Why the class's turns are shown apart, when they are. */
  incomparable: IncomparableReason | null;
  /** On `judged_blocks`: the block numbers judged in some turns and not in others. */
  judgedMismatch: number[];
  /** Whether the regulation tells judged blocks apart — what shows the judged share. */
  judgedBlocks: boolean;
}

const byName = (a: StandingsRow, b: StandingsRow) =>
  (a.entry.full_name ?? '').localeCompare(b.entry.full_name ?? '', undefined, { sensitivity: 'base' });

function section(
  key: string,
  kind: StandingsSection['kind'],
  rounds: StandingRound[],
  roundType: RoundType | null,
  entries: StandingEntry[],
  group: StandingGroup
): StandingsSection {
  // A turn shown apart keeps the tie-breaks of its own athletes: the group's
  // are the class's turns', and a tie is never across two turns ranked apart.
  const here = new Set(entries.map((entry) => entry.id));
  const ours = (ids: string[]) => ids.every((id) => here.has(id));

  const rows = entries
    .map((entry) => ({ entry, rank: entry.rank ?? null }))
    .sort((a, b) => {
      if (a.rank != null && b.rank != null) return a.rank - b.rank || byName(a, b);
      if (a.rank != null) return -1;
      if (b.rank != null) return 1;
      return byName(a, b);
    });

  const styles = new Set(
    entries.flatMap((entry) =>
      entry.cards.map((card) => card.climbed_as).filter((style) => style != null)
    )
  );

  const problems = rounds.flatMap((round) => round.problems);

  return {
    key,
    kind,
    rounds,
    roundType,
    rows,
    showTurn: kind === 'turns' && rounds.length > 1,
    mixedStyles: roundType === 'Lead' && styles.size > 1,
    judged: judgedIds(problems),
    judgedNumbers: [
      ...new Set(problems.filter((problem) => problem.requires_judge).map((problem) => problem.number)),
    ].sort((a, b) => a - b),
    tiebreaks: (group.tiebreaks ?? []).filter((tiebreak) =>
      ours(tiebreak.entries.map((entry) => entry.participant_id))
    ),
    ties: (group.ties ?? []).filter((tie) => ours(tie.participant_ids)),
    scopeRoundId: group.scope_round_id ?? null,
    canAddTiebreak: !!group.can_add_tiebreak,
    roundsNotEnded: (group.rounds_not_ended ?? []).map((round) => round.name),
  };
}

/** The class's turns: one table, or — when they cannot be merged — one each. */
function turnSections(group: StandingGroup): StandingsSection[] {
  if (group.comparable) {
    return [section('turns', 'turns', group.rounds, group.round_type, group.entries, group)];
  }

  // Each turn holds the people whose cards come from it, and those who have
  // climbed nowhere and booked nothing — who are on every turn's board.
  return group.rounds.map((round) =>
    section(
      `turn-${round.id}`,
      'turn',
      [round],
      round.round_type,
      group.entries.filter((entry) => entry.round_id === round.id || entry.round_id === null),
      group
    )
  );
}

/** Every table of a class's standings, in the order the page draws them. */
export function standingsLayout(standings: CategoryStandings): StandingsLayout {
  const sections: StandingsSection[] = [];

  if (standings.turns) sections.push(...turnSections(standings.turns));

  standings.rounds.forEach((group) => {
    const round = group.rounds[0];
    if (!round) return;
    sections.push(section(`round-${round.id}`, 'round', [round], group.round_type, group.entries, group));
  });

  const incomparable = standings.turns && !standings.turns.comparable ? standings.turns.reason : null;

  return {
    sections,
    incomparable,
    judgedMismatch: incomparable === 'judged_blocks' ? standings.turns?.judged_mismatch ?? [] : [],
    judgedBlocks: !!standings.ranking_judged_blocks,
  };
}
